import { createPortal } from "react-dom";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FullscreenModal, Button, CalendarInput, DropFileInput } from "shared";
import {
  useCloseAsset,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import "./CloseLoanModal.scss";

interface CloseLoanModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
}

interface FormValues {
  closedAt: string;
  contractFile: File | null;
}

const validationSchema = Yup.object({
  closedAt: Yup.string().required("Data zamknięcia jest wymagana"),
  contractFile: Yup.mixed().nullable(),
});

const initialValues: FormValues = {
  closedAt: "",
  contractFile: null,
};

const CloseLoanModal = ({ isOpen, onClose, item }: CloseLoanModalProps) => {
  const { mutateAsync: closeAsset, isPending } = useCloseAsset();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const handleSubmit = async (values: FormValues) => {
    if (!item?.uuid) return;

    try {
      await closeAsset(
        {
          uuid: item.uuid,
          closedAt: values.closedAt,
        },
        {
          onSuccess: async () => {
            if (values.contractFile instanceof File) {
              try {
                const userId: string | number =
                  (me as any)?.user?.id ?? (me as any)?.id ?? "";

                const uploaded = await uploadFile({
                  file: values.contractFile,
                  type: "attachment",
                  parentId: userId,
                });

                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;

                if (fileUuid && item.uuid) {
                  await attachFile({
                    investmentUuid: item.uuid,
                    fileUuid,
                    role: "closure_contract",
                  });
                }
              } catch {
                // Plik nie blokuje zamknięcia pożyczki
              }
            }

            onClose();
          },
        },
      );
    } catch {
      // Błąd jest obsługiwany przez hook
    }
  };

  if (!item) return null;

  return createPortal(
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="close-loan-modal">
        <h2 className="fullscreen-modal__header">Zamknij pożyczkę</h2>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="close-loan-modal__form">
              <div className="close-loan-modal__fields">
                <div className="close-loan-modal__field">
                  <Field
                    type="date"
                    name="closedAt"
                    label="Data zamknięcia"
                    component={CalendarInput}
                    errors={formik.errors}
                  />
                </div>
                <div className="close-loan-modal__field">
                  <Field
                    label="Umowa"
                    name="contractFile"
                    id="contractFile"
                    as={DropFileInput}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    optional
                  />
                </div>
              </div>
              <div className="close-loan-modal__actions">
                <Button
                  type="button"
                  onClick={onClose}
                  label="Anuluj"
                  className="button--stroke button--rounded"
                />
                <Button
                  type="submit"
                  onClick={() => {}}
                  label={isPending ? "Zapisywanie..." : "Potwierdź"}
                  className="button--rounded button--black"
                  isLoading={isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </FullscreenModal>,
    document.body,
  );
};

export default CloseLoanModal;
